// La columna de configuración del modo Catálogo IA, en desktop.
//
// Es el mismo diseño que estrenó el modo Modelo (ADR-2026-08-21-modelo-v2-una-sola-columna):
// UNA sola columna con todo lo que el modo necesita de arriba abajo —qué productos, qué
// vistas, los ajustes y el Generar—, en vez de repartirlo entre dos pasos y una grilla
// que ocupa media pantalla todo el tiempo. Lo que se elige se abre en el panel lateral
// (`PanelLateralModo`), sobre el área de la derecha.
//
// Lo que cambia respecto de Modelo es QUÉ se elige: acá no hay modelo ni pose, hay
// VISTAS, y la tanda sale de combinar cada vista con cada producto. Ver
// ADR-2026-08-24-catalogo-toma-el-diseno-de-modelo.
//
// Las dos elecciones se dibujan con cards que nacieron de las del modo Modelo
// (ADR-2026-08-24-catalogo-cards-como-modelo): fondo gris, recuadro con lo elegido y
// chevron. Ya no son las mismas: en Modelo van cuadradas y de a dos por fila, y acá son
// HORIZONTALES y apiladas desde que la columna del paso 2 bajó a 210px
// (ADR-2026-08-27-el-panel-del-paso-2-se-va-a-la-mitad).

import React from 'react';
import { Camera, ChevronRight, Sparkles, Type as TypeIcon } from 'lucide-react';
import CachedImg from '../../../components/CachedImg';
import {
  CalidadSegmentada,
  CampoInstruccion,
  CeldaAjuste,
  ValorConChevron,
  type CalidadImagen,
} from './AjustesRapidosProducto';
import { fondoSelectionLabel, type FondoSelection } from '../FondoPicker';
import { PieConfiguracionModo } from './PieConfiguracionModo';
import type { MockProductV2 } from '../mockData';
import { usePalabraDeModelo } from '../../../lib/nombreDeModelo';

/** Cuántas miniaturas entran en una card antes de resumir en "+N". */
const PREVIEWS_EN_CARD = 4;

/**
 * Las ilustraciones que muestra la card de productos cuando no hay nada elegido.
 *
 * Son assets y no íconos de la librería: la card muestra fotos de producto, y un trazo
 * de `lucide` al lado de una foto se lee como un ícono de sistema y no como "acá iría un
 * producto". Las mismas que usa el modo Modelo.
 */
const PLACEHOLDERS_PRODUCTO = [
  '/images/modo_con_modelo/placeholder.png',
  '/images/modo_con_modelo/jean_placeholder.png',
  '/images/modo_con_modelo/zapatillas_placeholder.png',
];

/** Cuánto se inclina cada ilustración del abanico, en grados (una a cada lado). */
const ANGULO_PLACEHOLDER = 10;

export interface PanelCatalogoCompactoProps {
  products: MockProductV2[];
  onAbrirProductos: () => void;
  onSubirArchivos: (files: FileList | File[]) => void;

  /**
   * Calidad. OPCIONAL: "Fotos de producto" no la ofrece en esta columna
   * (ADR-2026-09-04-fotos-de-producto-sin-calidad-en-la-columna). Sin las dos props la
   * celda no se dibuja; el Catálogo las pasa y la ve como siempre.
   */
  calidad?: CalidadImagen;
  onCalidadChange?: (v: CalidadImagen) => void;
  /** El fondo se dibuja como card con foto, así que necesita la selección entera. */
  fondoSelection: FondoSelection | null;
  onOpenFondo: () => void;
  /**
   * El modelo de la tanda, cuando alguna vista lo usa.
   *
   * Vive acá desde que el review dejó de dibujar sus cards globales
   * (ADR-2026-08-27-catalogo-el-review-convive-con-el-panel): sin esto, sacarlas de la
   * derecha dejaba al modelo sin ningún lugar donde elegirse. Con `undefined` la card no
   * se dibuja — es lo que pasa mientras la tanda sea toda de vistas solo-producto, que
   * no usan modelo.
   */
  modelo?: { label: string; imageUrl?: string | null } | null;
  onOpenModelo?: () => void;

  /** Cuántas imágenes salen: producto × vista. Va en el texto del CTA por defecto. */
  imagenesDeLaTanda: number;
  /**
   * Texto del CTA. Sin esto dice "Generar N imágenes".
   *
   * El paso de vistas dejó de ser el último del modo: lleva al review
   * (ADR-2026-08-27-catalogo-vuelve-el-review), así que su botón dice "Revisar catálogo"
   * y no promete una generación que todavía no ocurre.
   */
  ctaLabel?: string;
  creditCost: number | null;
  onGenerate: () => void;
  /** Con algo sin elegir el CTA se apaga y esto dice qué falta. */
  falta?: string | null;
  /**
   * Aclaración bajo el CTA cuando NO falta nada.
   *
   * La usa el paso 3 para la bajada del review ("Vamos a generar N imágenes. Tocá la X
   * de cualquier card para descartarla."): dice qué va a pasar al apretar y cómo sacar
   * una card, y las dos cosas se leen acá —al lado del botón— y no arriba del todo de la
   * otra columna (ADR-2026-08-27-la-bajada-del-review-va-con-el-generar). Cede el lugar a
   * `falta`: con algo pendiente, lo que hay que decir es qué falta.
   */
  nota?: React.ReactNode;

  // ── Lo que agrega "Fotos de producto" ─────────────────────────────────────
  // ADR-2026-09-04-fotos-de-producto-usa-la-columna-compacta-del-catalogo. El modo usa
  // esta misma columna, pero su Fondo no es el de la biblioteca (es liso de cuatro
  // colores o una escena descrita) y su Formato e instrucción se eligen en este paso —en
  // el Catálogo salieron de acá por ADR-2026-08-27-el-paso-de-vistas-se-queda-con-calidad—.
  // Todo opcional: sin estas props la columna es exactamente la del Catálogo.
  /** Pisa el valor de la card de Fondo, para el modo que tiene su propio vocabulario. */
  fondoValor?: string;
  /** Pisa lo que se dibuja en el recuadro de Fondo cuando no hay foto. */
  fondoVacio?: React.ReactNode;
  /** Formato como celda de ajuste, al lado de Calidad. */
  formatoLabel?: string;
  onOpenFormato?: () => void;
  /**
   * Las VISTAS como card, para el modo que las elige en el panel lateral y no en la
   * columna de al lado (ADR-2026-09-06-las-vistas-de-fotos-de-producto-van-al-panel-lateral).
   *
   * Sin `onOpenVistas` la card no se dibuja: el Catálogo sigue sin ella —ahí el picker
   * ES la columna de enfrente, y una card sería el mismo control dos veces
   * (ADR-2026-08-27-vistas-no-tiene-card-en-el-panel).
   */
  vistasValor?: string;
  vistasImagenes?: string[];
  vistasExtra?: string;
  onOpenVistas?: () => void;
  /** Instrucción libre, debajo de los ajustes. */
  instruccion?: string;
  onInstruccionChange?: (v: string) => void;
  /**
   * La columna en DOS piezas: lo que scrollea arriba y un PIE fijo con Calidad, Formato
   * y el Generar, como el del modo Modelo
   * (ADR-2026-09-06-el-pie-de-configuracion-es-un-componente).
   *
   * Es un flag y no la única forma porque esta columna la comparten dos modos: Fotos de
   * producto la pidió así y el Catálogo se queda con la columna que scrollea entera —sus
   * pasos 2 y 3 tienen otro CTA y otra bajada, y cambiarles el pie no era parte del
   * pedido—. Prendido, quien monta esta columna NO puede ser el scroller: tiene que
   * darle el alto (`flex flex-col`, no `overflow-y-auto`) o el pie se va de la vista.
   */
  pieFijo?: boolean;
}

/**
 * Card HORIZONTAL: recuadro a la izquierda, etiqueta y valor a la derecha, chevron al
 * final. La misma forma que la card de Productos, así las tres decisiones del modo se
 * leen como una lista y no como dos formatos mezclados.
 *
 * EXPORTADA porque la usa también "Animar imagen" para su card de la imagen elegida
 * (ADR-2026-09-07-animar-elige-la-imagen-con-la-card-y-el-panel-lateral). Es la misma
 * decisión —qué entra en la tanda, con su miniatura y su disparador— y rehacerla allá
 * habría dejado dos cards iguales que se despegan en el primer retoque.
 *
 * Era cuadrada —miniatura grande arriba, texto abajo, las dos en una fila— hasta que la
 * columna bajó a 210px: ahí dos cuadradas lado a lado daban 85px cada una y la miniatura
 * dejaba de mostrar nada. Apiladas y horizontales entran enteras y el recuadro se lee
 * (ADR-2026-08-27-el-panel-del-paso-2-se-va-a-la-mitad).
 *
 * Van con foto las decisiones que no se resumen en una palabra —"3 vistas" no dice
 * cuáles— y que son las que más se cambian; el resto del panel son filas de texto.
 */
export const CardPicker: React.FC<{
  etiqueta: string;
  valor: string;
  /** Miniaturas de lo elegido. Varias = mosaico; vacío = `vacio`. */
  imagenes: string[];
  /** Celda final del mosaico cuando hay más de las que entran, p. ej. "+3". */
  extra?: string;
  /** Qué se dibuja en el recuadro cuando no hay ninguna miniatura. */
  vacio?: React.ReactNode;
  onClick: () => void;
  /** La card está recibiendo un archivo arrastrado encima. */
  resaltado?: boolean;
  onDragOver?: React.DragEventHandler<HTMLButtonElement>;
  onDragLeave?: React.DragEventHandler<HTMLButtonElement>;
  onDrop?: React.DragEventHandler<HTMLButtonElement>;
  /**
   * Un control PROPIO al final de la card, a la izquierda del chevron — lo usa la card
   * de la plantilla para alojar el interruptor Imagen/Video
   * (ADR-2026-09-08-el-switch-de-imagen-o-video-va-en-la-card-de-la-plantilla).
   *
   * Con esto la card deja de ser un `<button>` y pasa a ser un `<div>` con el disparador
   * como capa `absolute inset-0` debajo: un botón dentro de otro botón es HTML inválido
   * y, peor, tocar el interruptor abriría también el panel lateral.
   */
  accesorio?: React.ReactNode;
}> = ({
  etiqueta,
  valor,
  imagenes,
  extra,
  vacio,
  onClick,
  resaltado = false,
  onDragOver,
  onDragLeave,
  onDrop,
  accesorio,
}) => {
  // `shrink-0`: la card mide 76px FIJOS (ADR-2026-08-25-card-de-productos-de-alto-fijo).
  // Dentro de una columna flex que no entra en la pantalla —los paneles de mobile—, sin
  // esto el flex se los cobra achicándola: la foto se aplasta y el nombre se corta,
  // en vez de que la columna scrollee.
  const clases = `w-full shrink-0 rounded-2xl transition-colors px-3 h-[76px] flex items-center gap-3 text-left ${
    resaltado
      ? 'bg-brand-primary/[0.06] ring-2 ring-brand-primary/40'
      : 'bg-black/[0.035] hover:bg-black/[0.06]'
  }`;
  const cuerpo = (
    <>
    {/* `relative` + hijos en `absolute inset-0`: con `place-items-center` el mosaico no
        se estiraba al recuadro sino que lo estiraba a él, y las filas salían de 33px en
        una caja de 52 (medido) — el `overflow-hidden` recortaba la celda del "+N".
        Anclado, las cuatro celdas miden 26 y entran. */}
    <div className="relative w-[52px] h-[52px] shrink-0 rounded-xl overflow-hidden bg-white grid place-items-center">
      {imagenes.length > 1 || extra ? (
        // Con varias elegidas la card las muestra TODAS —hasta cuatro, y la cuarta celda
        // pasa a ser el "+N" cuando hay más—: en este modo cada vista extra es una imagen
        // más por producto que se genera y se paga, así que cuántas van es parte de lo
        // que la card tiene que decir. El valor de al lado dice el número; esto, cuáles.
        <div className="absolute inset-0 grid grid-cols-2 grid-rows-2 gap-px bg-black/[0.06]">
          {imagenes.slice(0, extra ? 3 : 4).map((url, i) => (
            <CachedImg
              key={`${url}-${i}`}
              src={url}
              alt=""
              className="min-w-0 min-h-0 w-full h-full object-cover bg-white"
            />
          ))}
          {extra && (
            // `text-[10px]`: la celda del mosaico mide 26px de lado desde que el
            // recuadro bajó a 52; a 13px el "+12" se salía de su celda.
            <span className="min-w-0 min-h-0 grid place-items-center bg-white text-[10px] font-semibold text-brand-black/55">
              {extra}
            </span>
          )}
        </div>
      ) : imagenes.length === 1 ? (
        <CachedImg
          src={imagenes[0]}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
      ) : (
        vacio
      )}
    </div>
    <div className="min-w-0 flex-1">
      <p className="text-[14px] font-bold text-brand-black/90 leading-tight truncate">
        {etiqueta}
      </p>
      <p className="text-[13px] text-brand-black/50 leading-tight mt-0.5 truncate">
        {valor}
      </p>
    </div>
    </>
  );

  // Sin accesorio, la card ES el botón: la forma de siempre, sin capas.
  if (!accesorio) {
    return (
      <button
        type="button"
        // Marca al disparador para que el click-afuera del panel lo ignore — ver
        // `PanelLateralModo`.
        data-abre-panel-lateral
        onClick={onClick}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        className={clases}
      >
        {cuerpo}
        <ChevronRight className="w-4 h-4 shrink-0 text-brand-black/30" />
      </button>
    );
  }

  return (
    <div
      className={`relative ${clases}`}
      onDragOver={onDragOver as React.DragEventHandler<HTMLDivElement> | undefined}
      onDragLeave={onDragLeave as React.DragEventHandler<HTMLDivElement> | undefined}
      onDrop={onDrop as React.DragEventHandler<HTMLDivElement> | undefined}
    >
      {/* El disparador, DEBAJO de todo: cubre la card entera menos lo que se dibuje
          encima (el accesorio y el chevron, que van `relative`). Así tocar el
          interruptor no abre el panel. */}
      <button
        type="button"
        data-abre-panel-lateral
        onClick={onClick}
        aria-label={`${etiqueta}: ${valor}`}
        className="absolute inset-0 rounded-2xl"
      />
      {/* `pointer-events-none`: los clicks de esta zona los recibe el botón de atrás. */}
      <div className="relative pointer-events-none flex min-w-0 flex-1 items-center gap-3">
        {cuerpo}
      </div>
      <div className="relative shrink-0">{accesorio}</div>
      <ChevronRight className="relative w-4 h-4 shrink-0 text-brand-black/30" />
    </div>
  );
};

/**
 * La card de Productos: preview de lo elegido y disparador de la biblioteca.
 *
 * Es `CardPicker` con dos cosas propias: acepta archivos soltados encima (el `onDrop`
 * vive en la card, así que arrastrar funciona sin que un borde punteado tenga que
 * anunciarlo) y, cuando no hay nada elegido, dibuja una ilustración en vez del hueco.
 *
 * Antes era un componente aparte con su propia fila de cuatro recuadros de 52px. Esa
 * fila medía 226px y la columna del paso 2 tiene 178 de ancho útil desde que bajó a 210
 * (ADR-2026-08-27-el-panel-del-paso-2-se-va-a-la-mitad); reusando `CardPicker` las tres
 * decisiones del modo quedan además con la misma forma.
 */
const CardProductos: React.FC<{
  products: MockProductV2[];
  onAbrirProductos: () => void;
  onSubirArchivos: (files: FileList | File[]) => void;
}> = ({ products, onAbrirProductos, onSubirArchivos }) => {
  const [arrastrando, setArrastrando] = React.useState(false);
  // `slice(-4).reverse()`: lo último que se sumó va primero, que es lo que el usuario
  // acaba de tocar y lo que espera ver. Con más de cuatro, la última celda del mosaico
  // pasa a ser el "+N", así que entran tres.
  const visibles = products
    .slice(-PREVIEWS_EN_CARD)
    .reverse()
    .slice(
      0,
      products.length > PREVIEWS_EN_CARD
        ? PREVIEWS_EN_CARD - 1
        : PREVIEWS_EN_CARD,
    );
  return (
    <CardPicker
      etiqueta="Productos"
      // "N elegidos" y no "N productos": la etiqueta de arriba ya dice de qué son, y a
      // 13px en los ~66px que deja la card, "4 productos" se cortaba.
      valor={
        products.length === 0
          ? 'Sin elegir'
          : products.length === 1
            ? products[0].name
            : `${products.length} elegidos`
      }
      // Un producto sin foto va con la ilustración y no con el recuadro blanco vacío,
      // que no se distinguía de un hueco del layout.
      imagenes={visibles.map((p) => p.imageUrl || PLACEHOLDERS_PRODUCTO[0])}
      extra={
        products.length > PREVIEWS_EN_CARD
          ? `+${products.length - (PREVIEWS_EN_CARD - 1)}`
          : undefined
      }
      // UNA ilustración y no el abanico de tres: son un EJEMPLO de lo que va a haber ahí
      // —"tus productos van acá"—, y tres de 50px solapadas medían 108, más que el
      // recuadro entero.
      vacio={
        <img
          src={PLACEHOLDERS_PRODUCTO[0]}
          alt=""
          className="w-full h-full object-cover"
        />
      }
      onClick={onAbrirProductos}
      resaltado={arrastrando}
      onDragOver={(e) => {
        e.preventDefault();
        setArrastrando(true);
      }}
      onDragLeave={() => setArrastrando(false)}
      onDrop={(e) => {
        e.preventDefault();
        setArrastrando(false);
        if (e.dataTransfer.files?.length) onSubirArchivos(e.dataTransfer.files);
      }}
    />
  );
};

/**
 * Qué se ve en el recuadro de la card de Fondo cuando el fondo elegido no tiene foto.
 *
 * "Automático" y "descrito con palabras" NO son huecos —son decisiones tomadas—, así que
 * la card no puede caer en el "Sin elegir" genérico. Y "Fondo blanco" no necesita ícono:
 * el recuadro blanco vacío ES la preview literal de lo elegido.
 */
const vacioFondo = (sel: FondoSelection | null): React.ReactNode => {
  if (!sel || sel.mode === 'auto')
    return <Sparkles className="w-6 h-6 text-brand-black/25" />;
  if (sel.mode === 'descripcion')
    return <TypeIcon className="w-6 h-6 text-brand-black/25" />;
  return null;
};

export const PanelCatalogoCompacto: React.FC<PanelCatalogoCompactoProps> = ({
  products,
  onAbrirProductos,
  onSubirArchivos,
  calidad,
  onCalidadChange,
  fondoSelection,
  onOpenFondo,
  modelo,
  onOpenModelo,
  imagenesDeLaTanda,
  ctaLabel,
  nota,
  creditCost,
  onGenerate,
  falta,
  fondoValor,
  fondoVacio,
  formatoLabel,
  onOpenFormato,
  vistasValor,
  vistasImagenes,
  vistasExtra,
  onOpenVistas,
  instruccion,
  onInstruccionChange,
  pieFijo = false,
}) => {
  // La palabra de pantalla para la persona de la foto — "Modelo" en Moda, "Avatar"
  // en el resto (ADR-2026-09-22-en-moda-el-modelo-se-llama-modelo).
  const palabra = usePalabraDeModelo();
  /**
   * Las celdas de Calidad y Formato: en el pie fijo cuando el modo lo pide, y en el
   * flujo —apiladas, arriba de la instrucción— cuando no.
   */
  const hayCalidad = calidad != null && !!onCalidadChange;
  const hayFormato = !!formatoLabel && !!onOpenFormato;

  const contenido = (
    <>
    {/* ── Productos ──────────────────────────────────────────────────────
        Arrastrar encima sube, tocar abre la biblioteca: son el mismo momento —"de qué
        producto es el catálogo"— y separarlos en dos controles obligaba a decidir antes
        de mirar. */}
    <CardProductos
      products={products}
      onAbrirProductos={onAbrirProductos}
      onSubirArchivos={onSubirArchivos}
    />

    {/* ── Las decisiones con foto ────────────────────────────────────────
        APILADAS bajo la de Productos, como cards donde se ve lo elegido y no como filas
        de texto: son lo que más se cambia, y se eligen mirando la foto.

        Iban lado a lado (igual que Modelo y Pose en el modo Modelo) hasta que la columna
        se angostó: dos por fila daban 85px cada una.

        En el CATÁLOGO, Vistas no tiene card en ningún paso: en el 2 el picker es la
        columna de al lado y en el 3 lo elegido se revisa card por card enfrente, así que
        en los dos era el mismo control dos veces en la misma pantalla
        (ADR-2026-08-27-vistas-no-tiene-card-en-el-panel). En Fotos de producto SÍ la
        tiene: ahí el picker se mudó al panel lateral y la columna de enfrente volvió a
        ser la muestra del modo, así que la card es el único lugar donde se ve y se
        cambia lo elegido (ADR-2026-09-06-las-vistas-de-fotos-de-producto-van-al-panel-lateral).
        Modelo va aparte, y sólo cuando alguna vista de la tanda lo usa. */}
    <div className="flex flex-col gap-2">
      {/* Modelo: sólo cuando la tanda puede usarlo. Ocupa el lugar que dejó la card de
          Vistas (ADR-2026-08-27-en-el-paso-2-el-modelo-ocupa-el-lugar-de-las-vistas). */}
      {modelo && onOpenModelo && (
        <CardPicker
          etiqueta={palabra.titulo}
          valor={modelo.label}
          imagenes={modelo.imageUrl ? [modelo.imageUrl] : []}
          vacio={<Sparkles className="w-6 h-6 text-brand-black/25" />}
          onClick={onOpenModelo}
        />
      )}
      {/* Vistas: SÓLO para el modo que las abre en el panel lateral. El Catálogo no
          pasa el handler y la fila queda igual que antes. */}
      {onOpenVistas && (
        <CardPicker
          etiqueta="Vistas"
          valor={vistasValor ?? 'Sin elegir'}
          imagenes={vistasImagenes ?? []}
          extra={vistasExtra}
          // Sin ninguna elegida, una cámara y no el hueco blanco: dice de qué se trata
          // la card antes de que haya algo que previsualizar.
          vacio={<Camera className="w-6 h-6 text-brand-black/25" />}
          onClick={onOpenVistas}
        />
      )}
      <CardPicker
        etiqueta="Fondo"
        valor={fondoValor ?? fondoSelectionLabel(fondoSelection)}
        // Sólo los fondos de biblioteca y los guardados tienen foto; el resto cae en el
        // placeholder que le corresponde a cada uno.
        imagenes={
          fondoSelection?.mode === 'biblioteca' ||
          fondoSelection?.mode === 'guardado'
            ? [fondoSelection.imageUrl]
            : []
        }
        vacio={fondoVacio ?? vacioFondo(fondoSelection)}
        onClick={onOpenFondo}
      />
    </div>

    {/* ── Ajustes ────────────────────────────────────────────────────────
        Queda SÓLO Calidad. Cantidad, Formato y el textarea de indicaciones salieron del
        paso 2 (ADR-2026-08-27-el-paso-de-vistas-se-queda-con-calidad): la pantalla pide
        elegir vistas y esos tres la volvían una hoja de configuración.

        Fondo tampoco está acá: subió a la fila de cards
        (ADR-2026-08-24-catalogo-cards-como-modelo) y dibujarlo también como fila sería
        el mismo control dos veces en la misma pantalla.

        La CELDA es la misma que en Automático y Modelo v2 —etiqueta chica arriba,
        segmentado abajo— y no la fila de 48px con el nombre en negrita que tenía acá
        (ADR-2026-08-28-los-ajustes-se-ven-igual-en-los-tres-modos). A lo ancho de la
        columna porque es la única: media celda vacía al lado se lee como un control que
        falta cargar. */}
    <div className="flex flex-col gap-2">
      {/* Con pie fijo las dos celdas bajan ahí, en una fila de dos
          (ADR-2026-09-06-el-pie-de-configuracion-es-un-componente); acá quedan apiladas
          y a lo ancho, que es como las dibuja el Catálogo. */}
      {!pieFijo && hayCalidad && (
        <CeldaAjuste etiqueta="Calidad">
          <CalidadSegmentada calidad={calidad} onChange={onCalidadChange} />
        </CeldaAjuste>
      )}
      {/* Formato: sólo lo dibuja quien lo elige en este paso. El Catálogo no pasa las
          props y la columna queda igual que antes. */}
      {!pieFijo && hayFormato && (
        <CeldaAjuste etiqueta="Formato" onClick={onOpenFormato}>
          <ValorConChevron texto={formatoLabel} />
        </CeldaAjuste>
      )}
      {/* La indicación, debajo de los ajustes y desplegada: es el mismo campo —y el
          mismo historial— que en los otros modos (`CampoInstruccion`). */}
      {onInstruccionChange && (
        <CampoInstruccion
          valor={instruccion ?? ''}
          onChange={onInstruccionChange}
        />
      )}
    </div>

  </>
  );

  /**
   * Con pie fijo, la columna es la MISMA de dos piezas que el modo Modelo: un scroller
   * arriba y el pie abajo (`PieConfiguracionModo`). `h-full min-h-0` porque el alto lo
   * pone el contenedor de afuera, y sin `min-h-0` el scroller no puede achicarse y el
   * pie se empuja fuera de la vista — el bug clásico de flex + overflow.
   */
  if (pieFijo) {
    return (
      <div className="h-full min-h-0 flex flex-col">
        {/* El `pb-4` es el aire entre lo último del flujo y el pie. */}
        <div className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-4 pb-4">
          {contenido}
        </div>
        <PieConfiguracionModo
          calidad={calidad}
          onCalidadChange={onCalidadChange}
          formatoLabel={formatoLabel}
          onOpenFormato={onOpenFormato}
          // Sin contador: la tanda de estos modos es una CUENTA (producto × vista), no
          // un número que se elija.
          imagenesDeLaTanda={imagenesDeLaTanda}
          ctaLabel={ctaLabel}
          creditCost={creditCost}
          onGenerate={onGenerate}
          falta={falta}
          nota={nota}
        />
      </div>
    );
  }

  // Sin pie fijo: la columna scrollea entera y el Generar es el último bloque del flujo.
  return (
    <div className="flex flex-col gap-4">
      {contenido}
    <div className="flex flex-col gap-2">
      <button
        type="button"
        onClick={onGenerate}
        disabled={!!falta}
        className="w-full h-12 rounded-2xl bg-brand-primary text-white text-[15px] font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-40 disabled:pointer-events-none hover:opacity-90 transition-opacity"
      >
        {ctaLabel ??
          `Generar ${imagenesDeLaTanda} ${
            imagenesDeLaTanda === 1 ? 'imagen' : 'imágenes'
          }`}
        {creditCost != null && creditCost > 0 && (
          <span className="inline-flex items-center gap-1 bg-white text-brand-primary px-2 py-0.5 rounded-full text-[12.5px] font-semibold">
            <img
              src="/images/token_icon.png"
              alt=""
              aria-hidden="true"
              className="w-3.5 h-3.5 object-contain"
            />
            {creditCost}
          </span>
        )}
      </button>
      {/* El estado va DEBAJO del botón: arriba lo empujaba hacia abajo cada vez que
          aparecía o desaparecía, y el botón se movía bajo el cursor. */}
      {(falta || nota) && (
        <p className="text-[13px] text-brand-black/50 text-center leading-snug">
          {falta ?? nota}
        </p>
      )}
    </div>
    </div>
  );
};
